// Core packages
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, } from "@angular/forms";
// Third party packages
import { MatDialog } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { debounceTime, finalize, map, take } from "rxjs/operators";
// Custom packages
import { DataTypePreviewBuilder } from "src/app/modules/settings/data-types.preview";
import { capabilitiesOf, DATA_TYPE_LABEL_PATTERN, DATA_TYPE_NAME_PATTERN, diffFields, extractFieldLabelTokens, FIELD_LABEL_PREFIX, FIELD_TYPES, fieldIndexOfErrorPath, flattenTranslationKeys, MAX_FIELDS, MAX_OPTIONS, ON_CHANGE_HANDLERS, OPTIONS_SOURCES, parseErrorPath, POSITION_STEP, REQUIRED_WHEN_OPERATORS, SEARCH_SERVICES, } from "src/app/modules/settings/data-types.utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { DataTypesService } from "src/app/shared/services/data-types.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";
/** How many suggestions the i18n key autocompletes show at once */
var MAX_KEY_SUGGESTIONS = 30;
/** Field the creation form starts from: the most recurring one of the catalogue */
var STARTER_FIELD_NAME = "dataDocumento";
/**
 * Data types catalogue: the CRUD of the FORMS shown when a document is archived.
 *
 * Master-detail. The list is loaded without `fields[]` (by far the heaviest part of
 * the payload) and the full row is fetched only when one is opened.
 *
 * The editor is a Reactive Form, not Formly: the shape being edited is itself a form
 * definition, with a sortable array of fields each holding a nested array of options,
 * and the back-end reports validation errors as indexed paths ('fields[2].options[0]
 * .value') that have to land on one precise control. A FormArray maps onto that
 * one to one.
 *
 * The editor is built so that the schema rules of the back-end cannot be violated
 * rather than merely validated: positions are computed from the drag&drop order,
 * `options` and `optionsSource` are never offered together, and rule targets are
 * picked from a dropdown of sibling fields. The 400 handling is the safety net, not
 * the first line of defence.
 *
 * @since 3.0.0
 */
var DataTypesComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function DataTypesComponent(configService, cookieService, dataTypesService, dialog, formBuilder, helperService, themeService, toastrService, translateService) {
        this.configService = configService;
        this.cookieService = cookieService;
        this.dataTypesService = dataTypesService;
        this.dialog = dialog;
        this.formBuilder = formBuilder;
        this.helperService = helperService;
        this.themeService = themeService;
        this.toastrService = toastrService;
        this.translateService = translateService;
        this.subscriptions = [];
        /** Watcher of the editor, replaced every time the form is rebuilt */
        this.formSubscription = null;
        this.lang = this.translateService.currentLang || this.translateService.defaultLang;
        // BEGIN - List state
        this.dataTypes = [];
        this.visibleDataTypes = [];
        this.search = "";
        this.includeDeleted = false;
        this.listIsLoading = true;
        // END - List state
        // BEGIN - Editor state
        /** Row currently open, as it was loaded: the baseline of every diff */
        this.selected = null;
        this.detailIsLoading = false;
        /** True while editing a data type that does not exist on the back-end yet */
        this.isNew = false;
        this.form = this.buildEmptyForm();
        /** Per row collapse state, kept aligned with the fields array */
        this.fieldsExpanded = [];
        /** Names as of the last change, to propagate a rename into the rules */
        this.lastKnownNames = [];
        // END - Editor state
        // BEGIN - Live preview
        this.previewBuilder = new DataTypePreviewBuilder(this.translateService);
        this.previewForm = new FormGroup({});
        this.previewFields = [];
        this.previewModel = {};
        this.previewOptions = {};
        // END - Live preview
        // BEGIN - i18n key catalogues (autocompletes)
        /** Every key of the interface language, for `options[].label` */
        this.i18nKeys = [];
        /** Leaves under the archive prefix, for `fields[].label` */
        this.fieldLabelTokens = [];
        // END - i18n key catalogues
        // Closed vocabularies offered by the editor
        this.fieldTypes = FIELD_TYPES;
        this.optionsSources = OPTIONS_SOURCES;
        this.searchServices = SEARCH_SERVICES;
        this.requiredWhenOperators = REQUIRED_WHEN_OPERATORS;
        this.onChangeHandlers = ON_CHANGE_HANDLERS;
        this.maxFields = MAX_FIELDS;
        this.maxOptions = MAX_OPTIONS;
        /** Stable empty array for the free-text ng-select: see the caches below */
        this.noItems = [];
        this.saveBtnOptions = this.buildSaveButton();
        /**
         * Caches of the arrays the template binds to.
         *
         * A method returning a fresh array on every call breaks the development mode change
         * detection check (the second pass sees a different reference and throws
         * ExpressionChangedAfterItHasBeenCheckedError) and makes ng-select re-render on every
         * cycle. The values are still recomputed on every call - they depend on the whole
         * form - but the SAME reference is handed back while the content has not changed.
         */
        this.siblingsCache = [];
        this.targetOptionsCache = [];
        this.labelTokenMemo = null;
        this.i18nKeyMemo = null;
        /**
         * Uniqueness of `fields[].name` inside the data type, checked while typing.
         *
         * Declared as an arrow property so that `this` is bound when Angular calls it as a
         * plain validator function.
         *
         * @since 3.0.0
         */
        this.uniqueFieldName = function (control) {
            if (!control.parent || !control.parent.parent) {
                return null;
            }
            var array = control.parent.parent;
            var value = control.value ? control.value.toString() : "";
            if (!value) {
                return null;
            }
            var occurrences = 0;
            array.controls.forEach(function (group) {
                var sibling = group.get("name");
                if (sibling && sibling.value && sibling.value.toString() === value) {
                    occurrences++;
                }
            });
            return occurrences > 1 ? { duplicateName: true } : null;
        };
    }
    /**
     * Init component
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.ngOnInit = function () {
        this.themeService.setPageTitle(this.translateService.instant("settings.dataTypes.pageTitle"));
        this.lang =
            this.cookieService.get("lang") || this.configService.defaultLang;
        this.loadI18nCatalogues();
    };
    /**
     * After view init logic
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.ngAfterViewInit = function () {
        this.loadList();
    };
    /**
     * Handle component destroy
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    /**
     * Build the submit button state.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildSaveButton = function () {
        return {
            active: false,
            text: this.translateService.instant("common.cta.save"),
            spinnerSize: 19,
            raised: true,
            stroked: false,
            flat: false,
            fab: false,
            buttonColor: "primary",
            spinnerColor: "primary",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: { fontSet: "fal", fontIcon: "fa-save", inline: true },
        };
    };
    // BEGIN - i18n catalogues
    /**
     * Flatten the loaded translations once: the two autocompletes filter this list
     * instead of walking the translation tree on every keystroke.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.loadI18nCatalogues = function () {
        var store = this.translateService.store && this.translateService.store.translations
            ? this.translateService.store.translations
            : {};
        var translations = store[this.lang]
            ? store[this.lang]
            : store[this.translateService.defaultLang];
        this.i18nKeys = flattenTranslationKeys(translations);
        this.fieldLabelTokens = extractFieldLabelTokens(this.i18nKeys);
    };
    /**
     * Suggestions for a `fields[].label`: the TOKENS of the archive prefix, because that
     * is what gets stored, not the whole key.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.filterLabelTokens = function (term) {
        var key = term ? term.toString() : "";
        if (this.labelTokenMemo && this.labelTokenMemo.term === key) {
            return this.labelTokenMemo.result;
        }
        this.labelTokenMemo = {
            term: key,
            result: this.filterKeys(this.fieldLabelTokens, key),
        };
        return this.labelTokenMemo.result;
    };
    /**
     * Suggestions for an `options[].label`: full i18n keys.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.filterI18nKeys = function (term) {
        var key = term ? term.toString() : "";
        if (this.i18nKeyMemo && this.i18nKeyMemo.term === key) {
            return this.i18nKeyMemo.result;
        }
        this.i18nKeyMemo = {
            term: key,
            result: this.filterKeys(this.i18nKeys, key),
        };
        return this.i18nKeyMemo.result;
    };
    /**
     * Case insensitive "contains" filter, capped: an unbounded list of 3000 keys would
     * make the panel unusable and the change detection expensive.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.filterKeys = function (source, term) {
        var needle = term ? term.toString().toLowerCase() : "";
        var matches = [];
        for (var index = 0; index < source.length; index++) {
            if (!needle || source[index].toLowerCase().indexOf(needle) >= 0) {
                matches.push(source[index]);
                if (matches.length >= MAX_KEY_SUGGESTIONS) {
                    break;
                }
            }
        }
        return matches;
    };
    /**
     * Resolved text of a `fields[].label` token, '' when nothing is typed. Shown next to
     * the input so that the user sees the sentence behind the key.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.previewLabelToken = function (token) {
        if (!token) {
            return "";
        }
        return this.translateService.instant(FIELD_LABEL_PREFIX + token);
    };
    /**
     * Resolved text of a full i18n key.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.previewI18nKey = function (key) {
        if (!key) {
            return "";
        }
        return this.translateService.instant(key);
    };
    /**
     * True when the key does not exist in the catalogue: a warning, never a blocker.
     * The back-end accepts any string, and a key can legitimately be added to the
     * translation files later.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.labelTokenIsUnknown = function (token) {
        if (!token) {
            return false;
        }
        return this.fieldLabelTokens.indexOf(token.toString()) < 0;
    };
    /**
     * @see labelTokenIsUnknown
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.i18nKeyIsUnknown = function (key) {
        if (!key) {
            return false;
        }
        return this.i18nKeys.indexOf(key.toString()) < 0;
    };
    // END - i18n catalogues
    // BEGIN - List
    /**
     * Load the catalogue. `withFields=false`: the list needs names and codes only.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.loadList = function () {
        var _this = this;
        this.listIsLoading = true;
        this.subscriptions.push(this.dataTypesService
            .listSummaries(this.includeDeleted)
            .pipe(finalize(function () { return (_this.listIsLoading = false); }))
            .subscribe(function (res) {
            // Already ordered by dataTypeName: the front-end does not re-sort
            _this.dataTypes = res && Array.isArray(res.data) ? res.data : [];
            _this.applySearch();
        }, function (err) {
            _this.dataTypes = [];
            _this.visibleDataTypes = [];
            _this.helperService.handleError(err);
        }));
    };
    /**
     * Client side search on both the code and the technical name.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.applySearch = function () {
        var needle = this.search ? this.search.trim().toLowerCase() : "";
        if (!needle) {
            this.visibleDataTypes = this.dataTypes.slice();
            return;
        }
        this.visibleDataTypes = this.dataTypes.filter(function (dataType) {
            var label = dataType.dataTypeLabel
                ? dataType.dataTypeLabel.toLowerCase()
                : "";
            var name = dataType.dataTypeName
                ? dataType.dataTypeName.toLowerCase()
                : "";
            return label.indexOf(needle) >= 0 || name.indexOf(needle) >= 0;
        });
    };
    /**
     * Toggle the hidden rows.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onIncludeDeletedChange = function () {
        this.loadList();
    };
    /**
     * True when the given row is the one open in the editor.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.isSelected = function (dataType) {
        return !!(this.selected &&
            !this.isNew &&
            this.selected._id.toString() === dataType._id.toString());
    };
    // END - List
    // BEGIN - Selection
    /**
     * Open a data type, asking first when the editor holds unsaved work.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onSelect = function (dataType) {
        var _this = this;
        if (this.isSelected(dataType)) {
            return;
        }
        this.withDiscardConfirmation(function () { return _this.loadDetail(dataType._id); });
    };
    /**
     * Fetch the full row and build the editor on it.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.loadDetail = function (dataTypeId) {
        var _this = this;
        this.detailIsLoading = true;
        this.subscriptions.push(this.dataTypesService
            .get(dataTypeId)
            .pipe(finalize(function () { return (_this.detailIsLoading = false); }))
            .subscribe(function (res) {
            if (!res || !res.data) {
                _this.helperService.handleError(res);
                return;
            }
            _this.isNew = false;
            _this.selected = res.data;
            _this.buildForm(res.data);
        }, function (err) {
            _this.handleApiError(err);
        }));
    };
    /**
     * Start a new data type. It opens with one field already in place: a data type with
     * no field is refused by the back-end, and the archived date is the field almost
     * every schema of the catalogue begins with.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onNew = function () {
        var _this = this;
        this.withDiscardConfirmation(function () {
            _this.isNew = true;
            _this.selected = null;
            _this.buildForm({
                _id: "",
                dataTypeName: "",
                dataTypeLabel: "",
                deleted: false,
                fields: [
                    {
                        name: STARTER_FIELD_NAME,
                        position: POSITION_STEP,
                        type: "DateNotAvailable",
                        label: "dataDocumento",
                        required: true,
                        readonly: false,
                    },
                ],
            });
            _this.form.markAsDirty();
            _this.syncSaveButton();
        });
    };
    /**
     * Duplicate an existing data type: creating a variant of an existing schema is the
     * most frequent way a new one is born.
     *
     * Every `_id` of the source (the row, its fields, their options and rules) is
     * dropped: they belong to the source row, and a POST must not carry them.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onDuplicate = function (dataType) {
        var _this = this;
        this.withDiscardConfirmation(function () {
            _this.detailIsLoading = true;
            _this.subscriptions.push(_this.dataTypesService
                .get(dataType._id)
                .pipe(finalize(function () { return (_this.detailIsLoading = false); }))
                .subscribe(function (res) {
                if (!res || !res.data) {
                    _this.helperService.handleError(res);
                    return;
                }
                _this.isNew = true;
                _this.selected = null;
                _this.buildForm({
                    _id: "",
                    dataTypeName: res.data.dataTypeName + "Copy",
                    dataTypeLabel: _this.suggestCopyLabel(res.data.dataTypeLabel),
                    deleted: false,
                    fields: _this.stripIds(res.data.fields),
                });
                _this.form.markAsDirty();
                _this.syncSaveButton();
            }, function (err) {
                _this.handleApiError(err);
            }));
        });
    };
    /**
     * Code proposed for a copy, kept inside the 50 characters and the character set the
     * back-end accepts for `dataTypeLabel`.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.suggestCopyLabel = function (label) {
        var suffix = " (copia)";
        var source = label ? label : "";
        if (source.length + suffix.length <= 50) {
            return source + suffix;
        }
        return source.substring(0, 50 - suffix.length) + suffix;
    };
    /**
     * Deep copy of the fields with every `_id` removed.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.stripIds = function (fields) {
        return (Array.isArray(fields) ? fields : []).map(function (field) {
            var copy = JSON.parse(JSON.stringify(field));
            delete copy._id;
            if (Array.isArray(copy.options)) {
                copy.options.forEach(function (option) { return delete option._id; });
            }
            if (copy.requiredWhen) {
                delete copy.requiredWhen._id;
            }
            if (copy.onChange) {
                delete copy.onChange._id;
            }
            return copy;
        });
    };
    // END - Selection
    // BEGIN - Form building
    /**
     * An empty shell, so that the template can always bind something.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildEmptyForm = function () {
        return new FormGroup({
            dataTypeName: new FormControl(""),
            dataTypeLabel: new FormControl(""),
            fields: new FormArray([]),
        });
    };
    /**
     * Build the editor on a data type.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildForm = function (dataType) {
        var _this = this;
        this.form = this.formBuilder.group({
            dataTypeName: [
                dataType.dataTypeName,
                [Validators.required, Validators.pattern(DATA_TYPE_NAME_PATTERN)],
            ],
            dataTypeLabel: [
                dataType.dataTypeLabel,
                [
                    Validators.required,
                    Validators.maxLength(50),
                    Validators.pattern(DATA_TYPE_LABEL_PATTERN),
                ],
            ],
            fields: this.formBuilder.array((Array.isArray(dataType.fields) ? dataType.fields : []).map(function (field) {
                return _this.buildFieldGroup(field);
            })),
        });
        // One row open when there is only one, everything collapsed otherwise: a schema of
        // 20 fields fully expanded is unreadable.
        var count = this.fields.length;
        this.fieldsExpanded = [];
        for (var index = 0; index < count; index++) {
            this.fieldsExpanded.push(count === 1);
        }
        this.siblingsCache = [];
        this.targetOptionsCache = [];
        this.lastKnownNames = this.currentNames();
        // A different schema means different keys: keeping what was typed in the preview
        // of the previous one would carry values across two unrelated forms
        this.previewModel = {};
        this.watchForm();
        this.rebuildPreview();
        this.syncSaveButton();
    };
    /**
     * Keep the submit button in step with the editor: `mat-spinner-button` exposes no
     * `disabled` input of its own, its state lives inside the options object.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.syncSaveButton = function () {
        this.saveBtnOptions.disabled = !this.form.dirty;
    };
    /**
     * Build the FormGroup of a single field.
     *
     * UI-only controls (`_expandedRules`, `requiredWhenEnabled`, ...) are prefixed or
     * named so that the serializer, which is explicit about every key it sends, never
     * mistakes one for a payload attribute.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildFieldGroup = function (field) {
        var _this = this;
        var hasRequiredWhen = !!field.requiredWhen;
        var hasOnChange = !!field.onChange;
        return this.formBuilder.group({
            _id: [field._id ? field._id : ""],
            /** Remembers an explicitly stored empty default value, which "" cannot express */
            _hadDefaultValue: [typeof field.defaultValue !== "undefined"],
            /** Remembers the legacy mirror, so that it is kept in sync instead of dropped */
            _hadEnumValues: [Array.isArray(field.enumValues)],
            name: [
                field.name,
                [
                    Validators.required,
                    Validators.pattern(DATA_TYPE_NAME_PATTERN),
                    this.uniqueFieldName,
                ],
            ],
            type: [field.type, Validators.required],
            label: [field.label ? field.label : ""],
            required: [!!field.required],
            readonly: [!!field.readonly],
            minLength: [this.numberOrNull(field.minLength), Validators.min(0)],
            maxLength: [this.numberOrNull(field.maxLength), Validators.min(0)],
            min: [this.numberOrNull(field.min)],
            max: [this.numberOrNull(field.max)],
            defaultValue: [
                typeof field.defaultValue !== "undefined" ? field.defaultValue : "",
            ],
            optionsSource: [field.optionsSource ? field.optionsSource : ""],
            bindLabel: [field.bindLabel ? field.bindLabel : ""],
            bindValue: [field.bindValue ? field.bindValue : ""],
            multiple: [!!field.multiple],
            search: [field.search ? field.search : ""],
            options: this.formBuilder.array((Array.isArray(field.options) ? field.options : []).map(function (option) {
                return _this.buildOptionGroup(option);
            })),
            requiredWhenEnabled: [hasRequiredWhen],
            requiredWhen: this.formBuilder.group({
                _id: [
                    hasRequiredWhen && field.requiredWhen._id
                        ? field.requiredWhen._id
                        : "",
                ],
                field: [hasRequiredWhen ? field.requiredWhen.field : ""],
                operator: [hasRequiredWhen ? field.requiredWhen.operator : "eq"],
                values: [this.readRuleValues(field)],
            }),
            onChangeEnabled: [hasOnChange],
            onChange: this.formBuilder.group({
                _id: [hasOnChange && field.onChange._id ? field.onChange._id : ""],
                handler: [
                    hasOnChange ? field.onChange.handler : ON_CHANGE_HANDLERS[0],
                ],
                targetField: [hasOnChange ? field.onChange.targetField : ""],
                applyOnInit: [hasOnChange ? !!field.onChange.applyOnInit : false],
            }),
        });
    };
    /**
     * `requiredWhen.values` is a single control holding either a string (eq / ne) or an
     * array (in / nin): it is what the ng-select of the template binds to, and the
     * serializer normalises it back into the array the back-end wants.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.readRuleValues = function (field) {
        if (!field.requiredWhen || !Array.isArray(field.requiredWhen.values)) {
            return "";
        }
        var values = field.requiredWhen.values;
        if (this.operatorIsMulti(field.requiredWhen.operator)) {
            return values.slice();
        }
        return values.length ? values[0] : "";
    };
    /**
     * Build the FormGroup of one option of a closed list.
     *
     * `label` is deliberately NOT required: the back-end accepts an option without one,
     * and refusing to save a row that already has such an option would lock the user out
     * of a document the front-end alone considers wrong. It is flagged, not blocked.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildOptionGroup = function (option) {
        return this.formBuilder.group({
            _id: [option && option._id ? option._id : ""],
            value: [option ? option.value : "", Validators.required],
            label: [option && option.label ? option.label : ""],
        });
    };
    /**
     * Numbers arrive as numbers and leave as numbers; an absent attribute is null, which
     * the serializer turns into an omitted key.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.numberOrNull = function (value) {
        return typeof value === "number" ? value : null;
    };
    /**
     * React to every edit: refresh the preview, keep the name uniqueness consistent
     * across rows, and follow a rename into the rules pointing at it.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.watchForm = function () {
        var _this = this;
        // The form is rebuilt on every selection: without dropping the previous watcher
        // first they would pile up, one per data type opened in the session
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
        this.formSubscription = this.form.valueChanges
            .pipe(debounceTime(250))
            .subscribe(function () {
            _this.revalidateNames();
            _this.propagateRenames();
            _this.rebuildPreview();
            _this.syncSaveButton();
        });
        this.subscriptions.push(this.formSubscription);
    };
    /**
     * A duplicate involves two controls, but only the one being typed into is
     * revalidated by Angular: the other would keep a stale error forever. Revalidating
     * without emitting keeps this out of the valueChanges loop.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.revalidateNames = function () {
        this.fields.controls.forEach(function (group) {
            var control = group.get("name");
            if (control) {
                control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        });
    };
    /**
     * When a field is renamed, the rules of the other fields still point at the old
     * name: left alone they would be a 400 ("must reference an existing field") on a
     * form that looks perfectly fine. Following the rename keeps the schema coherent.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.propagateRenames = function () {
        var current = this.currentNames();
        var previous = this.lastKnownNames;
        this.lastKnownNames = current;
        if (previous.length !== current.length) {
            // A field was added or removed: indexes no longer line up, nothing to follow
            return;
        }
        var _loop_1 = function (index) {
            var from = previous[index];
            var to = current[index];
            if (!from || !to || from === to) {
                return "continue";
            }
            this_1.fields.controls.forEach(function (group) {
                var requiredWhen = group.get("requiredWhen.field");
                if (requiredWhen && requiredWhen.value === from) {
                    requiredWhen.setValue(to, { emitEvent: false });
                }
                var target = group.get("onChange.targetField");
                if (target && target.value === from) {
                    target.setValue(to, { emitEvent: false });
                }
            });
        };
        var this_1 = this;
        for (var index = 0; index < current.length; index++) {
            _loop_1(index);
        }
    };
    /**
     * Names currently in the editor, in order.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.currentNames = function () {
        return this.fields.controls.map(function (group) {
            var control = group.get("name");
            return control && control.value ? control.value.toString() : "";
        });
    };
    Object.defineProperty(DataTypesComponent.prototype, "fields", {
        // END - Form building
        // BEGIN - Fields editing
        /**
         * The fields array, typed.
         *
         * @since 3.0.0
         */
        get: function () {
            return this.form.get("fields");
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Options of the field at the given index.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.optionsOf = function (index) {
        return this.fields.at(index).get("options");
    };
    /**
     * What the type of the field at the given index supports. Drives which attributes
     * the row shows.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.capabilitiesAt = function (index) {
        var control = this.fields.at(index).get("type");
        return capabilitiesOf(control && control.value ? control.value : "");
    };
    /**
     * Add a field at the end, expanded, so that the user lands on what to fill in.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onAddField = function () {
        if (this.fields.length >= MAX_FIELDS) {
            this.toastrService.warning(this.translateService.instant("settings.dataTypes.errors.tooManyFields", {
                max: MAX_FIELDS,
            }));
            return;
        }
        this.fields.push(this.buildFieldGroup({
            name: "",
            position: 0,
            type: "String",
            required: false,
            readonly: false,
        }));
        this.fieldsExpanded.push(true);
        this.lastKnownNames = this.currentNames();
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Remove a field from the editor.
     *
     * Nothing is asked here: the consequence of removing a field that already exists is
     * spelled out once, at save time, together with every other rename and removal, so
     * that the user confirms the whole picture instead of a series of single steps.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onRemoveField = function (index) {
        if (this.fields.length <= 1) {
            this.toastrService.warning(this.translateService.instant("settings.dataTypes.errors.lastField"));
            return;
        }
        this.fields.removeAt(index);
        this.fieldsExpanded.splice(index, 1);
        this.lastKnownNames = this.currentNames();
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Persisted order is the displayed order: `position` is recomputed from it on save,
     * so a duplicated position cannot exist.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onFieldDropped = function (event) {
        if (event.previousIndex === event.currentIndex) {
            return;
        }
        var controls = this.fields.controls;
        moveItemInArray(controls, event.previousIndex, event.currentIndex);
        moveItemInArray(this.fieldsExpanded, event.previousIndex, event.currentIndex);
        // The array holds the same controls in a new order: tell it to re-emit
        this.fields.updateValueAndValidity();
        this.lastKnownNames = this.currentNames();
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Collapse or expand a row.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onToggleField = function (index) {
        this.fieldsExpanded[index] = !this.fieldsExpanded[index];
    };
    /**
     * Changing the type invalidates the attributes the new type does not support: they
     * are cleared right away, so that what the user sees is exactly what will be sent.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onTypeChange = function (index) {
        var group = this.fields.at(index);
        var caps = this.capabilitiesAt(index);
        if (!caps.minLength) {
            group.get("minLength").setValue(null, { emitEvent: false });
        }
        if (!caps.maxLength) {
            group.get("maxLength").setValue(null, { emitEvent: false });
        }
        if (!caps.min) {
            group.get("min").setValue(null, { emitEvent: false });
        }
        if (!caps.max) {
            group.get("max").setValue(null, { emitEvent: false });
        }
        if (!caps.defaultValue) {
            group.get("defaultValue").setValue("", { emitEvent: false });
            group.get("_hadDefaultValue").setValue(false, { emitEvent: false });
        }
        if (!caps.options) {
            var options = group.get("options");
            while (options.length) {
                options.removeAt(0);
            }
        }
        if (!caps.optionsSource) {
            group.get("optionsSource").setValue("", { emitEvent: false });
        }
        if (!caps.multiple) {
            group.get("multiple").setValue(false, { emitEvent: false });
        }
        if (!caps.remoteSearch) {
            group.get("bindLabel").setValue("", { emitEvent: false });
            group.get("bindValue").setValue("", { emitEvent: false });
            group.get("search").setValue("", { emitEvent: false });
        }
        if (!caps.requiredWhen) {
            // The back-end refuses a rule on this type: drop it instead of hiding it
            group.get("requiredWhenEnabled").setValue(false, { emitEvent: false });
        }
        this.form.markAsDirty();
        this.syncSaveButton();
        this.form.updateValueAndValidity();
    };
    /**
     * Add an option to a closed list.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onAddOption = function (index) {
        var options = this.optionsOf(index);
        if (options.length >= MAX_OPTIONS) {
            this.toastrService.warning(this.translateService.instant("settings.dataTypes.errors.tooManyOptions", { max: MAX_OPTIONS }));
            return;
        }
        options.push(this.buildOptionGroup({ value: "", label: "" }));
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Remove an option from a closed list.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onRemoveOption = function (fieldIndex, optionIndex) {
        this.optionsOf(fieldIndex).removeAt(optionIndex);
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Turn a rule on or off. Enabling `requiredWhen` pre-selects nothing: the target has
     * to be a deliberate choice.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onToggleRule = function (index, rule, enabled) {
        var group = this.fields.at(index);
        group.get(rule + "Enabled").setValue(enabled);
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * The values of a rule belong to the field it observes: pointing the rule at another
     * field makes them meaningless, so they are cleared instead of being carried over
     * into a comparison that can never match.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onRuleFieldChange = function (index) {
        var group = this.fields.at(index);
        var operator = group.get("requiredWhen.operator").value;
        group
            .get("requiredWhen.values")
            .setValue(this.operatorIsMulti(operator) ? [] : "");
        this.form.markAsDirty();
        this.syncSaveButton();
    };
    /**
     * Switching between a single value operator and a list one converts the value
     * instead of dropping it.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onOperatorChange = function (index) {
        var group = this.fields.at(index);
        var operator = group.get("requiredWhen.operator").value;
        var control = group.get("requiredWhen.values");
        var value = control.value;
        if (this.operatorIsMulti(operator)) {
            if (!Array.isArray(value)) {
                control.setValue(value ? [value] : []);
            }
            return;
        }
        if (Array.isArray(value)) {
            control.setValue(value.length ? value[0] : "");
        }
    };
    /**
     * True for the operators taking a list of values.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.operatorIsMulti = function (operator) {
        return operator === "in" || operator === "nin";
    };
    /**
     * True when the rule of the row at `index` takes a list.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.ruleIsMulti = function (index) {
        var control = this.fields.at(index).get("requiredWhen.operator");
        return this.operatorIsMulti(control ? control.value : "");
    };
    /**
     * Names of the OTHER fields: the only legal targets of a rule. Offering them as a
     * dropdown is what makes "the target must exist" impossible to get wrong.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.siblingNames = function (index) {
        var names = [];
        this.fields.controls.forEach(function (group, position) {
            if (position === index) {
                return;
            }
            var control = group.get("name");
            if (control && control.value) {
                names.push(control.value.toString());
            }
        });
        var cached = this.siblingsCache[index];
        if (cached && this.sameStrings(cached, names)) {
            return cached;
        }
        this.siblingsCache[index] = names;
        return names;
    };
    /**
     * True when the two lists hold the same strings in the same order.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.sameStrings = function (left, right) {
        if (left.length !== right.length) {
            return false;
        }
        for (var index = 0; index < left.length; index++) {
            if (left[index] !== right[index]) {
                return false;
            }
        }
        return true;
    };
    /**
     * Options of the field a `requiredWhen` points at, when it owns a closed list: the
     * values of the rule are then picked instead of typed, which removes the last free
     * text left in the rules.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.targetFieldOptions = function (index) {
        return this.cacheTargetOptions(index, this.computeTargetOptions(index));
    };
    /**
     * Options of the target field, recomputed from the form.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.computeTargetOptions = function (index) {
        var _this = this;
        var target = this.fields.at(index).get("requiredWhen.field");
        var name = target && target.value ? target.value.toString() : "";
        if (!name) {
            return [];
        }
        var match = this.fields.controls.filter(function (group) {
            var control = group.get("name");
            return !!(control && control.value && control.value.toString() === name);
        });
        if (!match.length) {
            return [];
        }
        var options = match[0].get("options");
        if (!options || !options.length) {
            return [];
        }
        return options.controls.map(function (group) {
            var value = group.get("value").value;
            var label = group.get("label").value;
            return {
                value: value,
                label: label ? _this.translateService.instant(label) : value,
            };
        });
    };
    /**
     * Hand back the cached array while its content has not changed. @see siblingsCache
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.cacheTargetOptions = function (index, computed) {
        var cached = this.targetOptionsCache[index];
        if (cached && cached.length === computed.length) {
            var identical = true;
            for (var position = 0; position < cached.length; position++) {
                if (cached[position].value !== computed[position].value ||
                    cached[position].label !== computed[position].label) {
                    identical = false;
                    break;
                }
            }
            if (identical) {
                return cached;
            }
        }
        this.targetOptionsCache[index] = computed;
        return computed;
    };
    /**
     * Summary shown on a collapsed row.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.fieldSummary = function (index) {
        var group = this.fields.at(index);
        var name = group.get("name").value;
        return name
            ? name
            : this.translateService.instant("settings.dataTypes.newField");
    };
    /**
     * True when the row holds at least one invalid control: the collapsed row has to say
     * so, or an error hidden inside a closed row is invisible.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.fieldIsInvalid = function (index) {
        var group = this.fields.at(index);
        return !!(group && group.invalid && (group.touched || group.dirty));
    };
    // END - Fields editing
    // BEGIN - Preview
    /**
     * Rebuild the live preview from what is currently in the editor.
     *
     * The model is deliberately kept across rebuilds: the point of the preview is to
     * play with the rules, and resetting the values on every keystroke would make that
     * impossible.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.rebuildPreview = function () {
        var fields = this.serializeFields();
        this.previewForm = new FormGroup({});
        this.previewOptions = {};
        this.previewFields = this.previewBuilder.build(fields);
    };
    /**
     * Empty the values played with in the preview.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onResetPreview = function () {
        this.previewModel = {};
        this.rebuildPreview();
    };
    // END - Preview
    // BEGIN - Serialization
    /**
     * Build the `fields[]` payload out of the editor.
     *
     * Two rules drive every decision here:
     *  - ApiService strips null values from the body, so an attribute is cleared by
     *    OMITTING its key, never by sending null;
     *  - the capabilities of the type decide what is sent, which is what guarantees the
     *    "options XOR optionsSource" rule instead of hoping the user did not fill both.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.serializeFields = function () {
        var _this = this;
        return this.fields.controls.map(function (group, index) {
            var raw = group.value;
            var caps = capabilitiesOf(raw.type);
            var field = {
                name: raw.name ? raw.name.toString().trim() : "",
                // Never typed by the user: the drag&drop order IS the position
                position: (index + 1) * POSITION_STEP,
                type: raw.type,
                required: !!raw.required,
                readonly: !!raw.readonly,
            };
            if (raw._id) {
                field._id = raw._id;
            }
            if (raw.label) {
                field.label = raw.label.toString().trim();
            }
            if (caps.minLength && _this.isFilledNumber(raw.minLength)) {
                field.minLength = Number(raw.minLength);
            }
            if (caps.maxLength && _this.isFilledNumber(raw.maxLength)) {
                field.maxLength = Number(raw.maxLength);
            }
            if (caps.min && _this.isFilledNumber(raw.min)) {
                field.min = Number(raw.min);
            }
            if (caps.max && _this.isFilledNumber(raw.max)) {
                field.max = Number(raw.max);
            }
            // '' is a meaningful default value, but only when it was explicitly stored:
            // an empty input on a field that never had one stays an omitted key.
            if (caps.defaultValue && (raw.defaultValue || raw._hadDefaultValue)) {
                field.defaultValue = raw.defaultValue ? raw.defaultValue : "";
            }
            if (caps.options) {
                var options = _this.serializeOptions(raw.options);
                if (options.length) {
                    field.options = options;
                    // Keep the legacy mirror in sync where it exists, instead of letting it rot
                    if (raw._hadEnumValues) {
                        field.enumValues = options.map(function (option) { return option.value; });
                    }
                }
            }
            if (caps.optionsSource && raw.optionsSource) {
                field.optionsSource = raw.optionsSource;
            }
            if (caps.multiple) {
                field.multiple = !!raw.multiple;
            }
            if (caps.remoteSearch) {
                if (raw.bindLabel) {
                    field.bindLabel = raw.bindLabel;
                }
                if (raw.bindValue) {
                    field.bindValue = raw.bindValue;
                }
                if (raw.search) {
                    field.search = raw.search;
                }
            }
            var requiredWhen = _this.serializeRequiredWhen(raw, caps);
            if (requiredWhen) {
                field.requiredWhen = requiredWhen;
            }
            if (raw.onChangeEnabled && raw.onChange.handler && raw.onChange.targetField) {
                field.onChange = {
                    handler: raw.onChange.handler,
                    targetField: raw.onChange.targetField,
                    applyOnInit: !!raw.onChange.applyOnInit,
                };
                if (raw.onChange._id) {
                    field.onChange._id = raw.onChange._id;
                }
            }
            return field;
        });
    };
    /**
     * Options of a closed list, empty ones dropped.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.serializeOptions = function (raw) {
        var options = [];
        (Array.isArray(raw) ? raw : []).forEach(function (option) {
            if (!option || !option.value) {
                return;
            }
            var serialized = {
                value: option.value.toString().trim(),
                label: option.label ? option.label.toString().trim() : "",
            };
            if (option._id) {
                serialized._id = option._id;
            }
            options.push(serialized);
        });
        return options;
    };
    /**
     * The conditional rule, normalised into the array shape the back-end wants and with
     * the arity its operator demands.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.serializeRequiredWhen = function (raw, caps) {
        if (!raw.requiredWhenEnabled || !caps.requiredWhen) {
            return null;
        }
        var rule = raw.requiredWhen;
        if (!rule || !rule.field || !rule.operator) {
            return null;
        }
        var values = [];
        if (this.operatorIsMulti(rule.operator)) {
            values = Array.isArray(rule.values) ? rule.values.slice() : [];
        }
        else if (rule.values) {
            values = [
                Array.isArray(rule.values) ? rule.values[0] : rule.values,
            ];
        }
        values = values.filter(function (value) { return value !== null && value !== ""; });
        if (!values.length) {
            return null;
        }
        var serialized = {
            field: rule.field,
            operator: rule.operator,
            values: values,
        };
        if (rule._id) {
            serialized._id = rule._id;
        }
        return serialized;
    };
    /**
     * True when a numeric control actually holds a number: an empty input is '' or null
     * and has to become an omitted key, not a 0.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.isFilledNumber = function (value) {
        return (value !== null &&
            typeof value !== "undefined" &&
            value !== "" &&
            !isNaN(Number(value)));
    };
    // END - Serialization
    // BEGIN - Saving
    /**
     * Save, after asking about anything that would orphan already archived data.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onSubmit = function () {
        var _this = this;
        if (!this.markFormAsTouched()) {
            return;
        }
        var fields = this.serializeFields();
        if (!fields.length) {
            this.toastrService.error(this.translateService.instant("settings.dataTypes.errors.lastField"));
            return;
        }
        if (this.isNew) {
            this.persistCreate({
                dataTypeName: this.form.get("dataTypeName").value,
                dataTypeLabel: this.form.get("dataTypeLabel").value,
                fields: fields,
            });
            return;
        }
        var payload = {
            dataTypeName: this.form.get("dataTypeName").value,
            dataTypeLabel: this.form.get("dataTypeLabel").value,
            fields: fields,
        };
        var warning = this.buildDestructiveWarning(fields, payload.dataTypeName);
        if (!warning) {
            this.persistUpdate(payload);
            return;
        }
        this.confirmThen(this.translateService.instant("settings.dataTypes.dialogs.orphan.title"), warning, function () { return _this.persistUpdate(payload); }, "warn");
    };
    /**
     * The message of the confirmation shown before a save that orphans data, or null
     * when the save is harmless.
     *
     * A removed or renamed field leaves the values already archived under its old name
     * in place: they keep the old key, stop being displayed and nothing raises an error.
     * It is the one thing in this page that cannot be undone from this page.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.buildDestructiveWarning = function (fields, nextName) {
        if (!this.selected) {
            return null;
        }
        var diff = diffFields(this.selected.fields, fields);
        var parts = [];
        if (diff.removed.length) {
            parts.push(this.translateService.instant("settings.dataTypes.dialogs.orphan.removed", {
                names: diff.removed.join(", "),
            }));
        }
        if (diff.renamed.length) {
            parts.push(this.translateService.instant("settings.dataTypes.dialogs.orphan.renamed", {
                names: diff.renamed
                    .map(function (entry) { return entry.from + " \u2192 " + entry.to; })
                    .join(", "),
            }));
        }
        if (nextName !== this.selected.dataTypeName) {
            parts.push(this.translateService.instant("settings.dataTypes.dialogs.orphan.renamedSchema", {
                from: this.selected.dataTypeName,
                to: nextName,
            }));
        }
        if (!parts.length) {
            return null;
        }
        parts.push(this.translateService.instant("settings.dataTypes.dialogs.orphan.consequence"));
        // The dialog renders its message through [innerHTML] (Angular sanitises it)
        return parts.join("<br /><br />");
    };
    /**
     * POST the new data type.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.persistCreate = function (payload) {
        var _this = this;
        this.saveBtnOptions.active = true;
        this.subscriptions.push(this.dataTypesService
            .create(payload)
            .pipe(finalize(function () { return (_this.saveBtnOptions.active = false); }))
            .subscribe(function (res) {
            _this.onSaved(res, "settings.dataTypes.created");
        }, function (err) {
            _this.handleApiError(err);
        }));
    };
    /**
     * PUT the open data type. `fields` is always sent whole: there is no endpoint to
     * touch a single field, and a partial array would replace the list with itself
     * truncated.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.persistUpdate = function (payload) {
        var _this = this;
        this.saveBtnOptions.active = true;
        this.subscriptions.push(this.dataTypesService
            .update(this.selected._id, payload)
            .pipe(finalize(function () { return (_this.saveBtnOptions.active = false); }))
            .subscribe(function (res) {
            _this.onSaved(res, "settings.dataTypes.saved");
        }, function (err) {
            _this.handleApiError(err);
        }));
    };
    /**
     * Rebuild the editor on what the back-end answered: it is the new baseline of every
     * further diff, and it carries the ids of the fields just created.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onSaved = function (res, fallbackMessageKey) {
        this.toastrService.success(res && res.message
            ? res.message
            : this.translateService.instant(fallbackMessageKey));
        if (res && res.data && res.data._id) {
            this.isNew = false;
            this.selected = res.data;
            this.buildForm(res.data);
        }
        this.loadList();
    };
    /**
     * Mark everything as touched and refuse an invalid submit, always saying why.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.markFormAsTouched = function () {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.expandFirstInvalidField();
            this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"));
            return false;
        }
        return true;
    };
    /**
     * Open the first row holding an error: an invalid control inside a collapsed row is
     * an error the user cannot see.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.expandFirstInvalidField = function () {
        for (var index = 0; index < this.fields.length; index++) {
            if (this.fields.at(index).invalid) {
                this.fieldsExpanded[index] = true;
                return;
            }
        }
    };
    // END - Saving
    // BEGIN - Hide / restore
    /**
     * Hide a data type. It is NOT a delete: the row stays, GET /:id keeps serving it and
     * the document types already bound to it keep rendering their form.
     *
     * The 409 answered when it is still referenced is a question, not a failure: it is
     * turned into a dialog listing the document types, with the option to hide it anyway.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onHide = function (dataType) {
        var _this = this;
        this.confirmThen(this.translateService.instant("settings.dataTypes.dialogs.hide.title"), this.translateService.instant("settings.dataTypes.dialogs.hide.message", {
            name: dataType.dataTypeLabel
                ? dataType.dataTypeLabel
                : dataType.dataTypeName,
        }), function () { return _this.performHide(dataType, false); }, "warn");
    };
    /**
     * Run the hide, escalating to the "in use" conversation on a 409.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.performHide = function (dataType, force) {
        var _this = this;
        this.subscriptions.push(this.dataTypesService.hide(dataType._id, force).subscribe(function (res) {
            _this.toastrService.success(res && res.message
                ? res.message
                : _this.translateService.instant("settings.dataTypes.hidden"));
            _this.afterVisibilityChange(dataType._id, res);
        }, function (err) {
            var status = err && typeof err.status !== "undefined" ? err.status : 0;
            if (status === 409 && !force) {
                _this.confirmInUse(dataType, err);
                return;
            }
            _this.handleApiError(err);
        }));
    };
    /**
     * The data type is still referenced: show WHAT is using it, straight from the
     * back-end message (it already lists the document type codes), and let the user
     * decide whether to hide it anyway.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.confirmInUse = function (dataType, err) {
        var _this = this;
        var detail = err && err.error && err.error.message ? err.error.message : "";
        var message = [
            this.translateService.instant("settings.dataTypes.dialogs.inUse.message"),
            detail,
            this.translateService.instant("settings.dataTypes.dialogs.inUse.consequence"),
        ]
            .filter(function (part) { return !!part; })
            // The dialog renders its message through [innerHTML] (Angular sanitises it)
            .join("<br /><br />");
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("settings.dataTypes.dialogs.inUse.title"),
                message: message,
                btnOkText: this.translateService.instant("settings.dataTypes.actions.hideAnyway"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
                btnOkColor: "warn",
                btnOkIcon: "fa-eye-slash",
            },
            width: "640px",
        });
        this.subscriptions.push(dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (confirmed) {
            if (confirmed) {
                _this.performHide(dataType, true);
            }
        }));
    };
    /**
     * Bring a hidden data type back.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.onRestore = function (dataType) {
        var _this = this;
        this.subscriptions.push(this.dataTypesService.restore(dataType._id).subscribe(function (res) {
            _this.toastrService.success(res && res.message
                ? res.message
                : _this.translateService.instant("settings.dataTypes.restored"));
            _this.afterVisibilityChange(dataType._id, res);
        }, function (err) {
            _this.handleApiError(err);
        }));
    };
    /**
     * Refresh the list after a hide or a restore, and keep the editor in sync when the
     * row it holds is the one that changed.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.afterVisibilityChange = function (dataTypeId, res) {
        if (this.selected &&
            !this.isNew &&
            this.selected._id.toString() === dataTypeId.toString() &&
            res &&
            res.data) {
            // Only the flag changed: refresh the baseline without throwing away the edits
            this.selected.deleted = !!res.data.deleted;
        }
        this.loadList();
    };
    // END - Hide / restore
    // BEGIN - Errors
    /**
     * Surface an API error the way this page needs it.
     *
     *  - 400 with `errors`: every path is mapped onto its control, the rows holding one
     *    are expanded and the first is scrolled to. A generic toast would hide WHERE.
     *  - 400 without `errors`: a schema rule the UI should have prevented. Shown as is.
     *  - 409: duplicated `dataTypeName`. Highlighted on the field, not toasted alone.
     *  - 404: the row is gone, the list is reloaded.
     *  - anything else: standard handling.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.handleApiError = function (err) {
        var status = err && typeof err.status !== "undefined" ? err.status : 0;
        var message = err && err.error && typeof err.error.message === "string"
            ? err.error.message
            : "";
        if (status === 400) {
            var mapped = this.applyServerFieldErrors(err);
            this.toastrService.error(message
                ? message
                : this.translateService.instant("settings.dataTypes.errors.invalid"));
            if (!mapped) {
                // Nothing to highlight: it is a schema rule, the message is all there is
                return;
            }
            return;
        }
        if (status === 409) {
            var control = this.form.get("dataTypeName");
            if (control) {
                control.markAsTouched();
                control.markAsDirty();
                control.setErrors({
                    "server-error": this.translateService.instant("settings.dataTypes.errors.nameConflict"),
                });
            }
            this.toastrService.error(message
                ? message
                : this.translateService.instant("settings.dataTypes.errors.nameConflict"));
            return;
        }
        if (status === 404) {
            this.toastrService.error(message
                ? message
                : this.translateService.instant("settings.dataTypes.errors.notFound"));
            this.selected = null;
            this.isNew = false;
            this.form = this.buildEmptyForm();
            this.loadList();
            return;
        }
        this.helperService.handleError(err);
    };
    /**
     * Map the express-validator `errors` map onto the controls it points at.
     *
     * @since 3.0.0
     *
     * @returns true when at least one control was flagged
     */
    DataTypesComponent.prototype.applyServerFieldErrors = function (err) {
        var _this = this;
        var errors = err && err.error ? err.error.errors : null;
        if (!errors || typeof errors !== "object") {
            return false;
        }
        var flagged = false;
        var firstFieldIndex = -1;
        Object.keys(errors).forEach(function (path) {
            var entry = errors[path];
            var msg = entry && typeof entry.msg === "string"
                ? entry.msg
                : _this.translateService.instant("settings.dataTypes.errors.invalid");
            var control = _this.controlFromPath(path);
            if (!control) {
                return;
            }
            control.markAsTouched();
            control.markAsDirty();
            control.setErrors({ "server-error": msg });
            flagged = true;
            var index = fieldIndexOfErrorPath(path);
            if (index >= 0 && index < _this.fieldsExpanded.length) {
                _this.fieldsExpanded[index] = true;
                if (firstFieldIndex < 0) {
                    firstFieldIndex = index;
                }
            }
        });
        if (firstFieldIndex >= 0) {
            this.scrollToField(firstFieldIndex);
        }
        return flagged;
    };
    /**
     * Walk an indexed error path down to the control it names.
     *
     * Stops on the closest reachable control rather than giving up: a path pointing
     * inside a value the editor models as a single control (the values of a rule) still
     * highlights that control.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.controlFromPath = function (path) {
        var segments = parseErrorPath(path);
        var control = this.form;
        for (var index = 0; index < segments.length; index++) {
            var segment = segments[index];
            if (control instanceof FormArray && typeof segment === "number") {
                if (segment < 0 || segment >= control.length) {
                    return null;
                }
                control = control.at(segment);
                continue;
            }
            if (control instanceof FormGroup && typeof segment === "string") {
                var child = control.get(segment);
                if (!child) {
                    return control === this.form ? null : control;
                }
                control = child;
                continue;
            }
            // A leaf reached before the path ended: highlight what we have
            return control;
        }
        return control;
    };
    /**
     * Bring a field row into view, once the expansion has been rendered.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.scrollToField = function (index) {
        setTimeout(function () {
            var element = document.getElementById("dt-field-" + index);
            if (element && typeof element.scrollIntoView === "function") {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 150);
    };
    Object.defineProperty(DataTypesComponent.prototype, "hasPendingChanges", {
        // END - Errors
        // BEGIN - Pending changes
        /**
         * True when the editor holds work that has not been sent.
         *
         * @since 3.0.0
         */
        get: function () {
            return !!(this.selected || this.isNew) && this.form.dirty;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Route guard hook: block a navigation that would silently throw away the editor.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.canDeactivate = function () {
        if (!this.hasPendingChanges) {
            return true;
        }
        return this.askToDiscard();
    };
    /**
     * Run `action`, asking first when there is pending work.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.withDiscardConfirmation = function (action) {
        if (!this.hasPendingChanges) {
            action();
            return;
        }
        this.subscriptions.push(this.askToDiscard().subscribe(function (confirmed) {
            if (confirmed) {
                action();
            }
        }));
    };
    /**
     * The "discard your changes?" dialog, as an Observable of the answer.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.askToDiscard = function () {
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("settings.dataTypes.dialogs.discard.title"),
                message: this.translateService.instant("settings.dataTypes.dialogs.discard.message"),
                btnOkText: this.translateService.instant("settings.dataTypes.actions.discard"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
                btnOkColor: "warn",
                btnOkIcon: "fa-trash-alt",
            },
            width: "560px",
        });
        return dialogRef
            .beforeClosed()
            .pipe(take(1))
            .pipe(map(function (confirmed) { return !!confirmed; }));
    };
    // END - Pending changes
    // BEGIN - Shared helpers
    /**
     * Open a confirmation dialog and run `action` when the user confirms.
     *
     * @since 3.0.0
     */
    DataTypesComponent.prototype.confirmThen = function (title, message, action, okColor) {
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: title,
                message: message,
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
                btnOkColor: okColor ? okColor : "primary",
            },
            width: "640px",
        });
        this.subscriptions.push(dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (confirmed) {
            if (confirmed) {
                action();
            }
        }));
    };
    return DataTypesComponent;
}());
export { DataTypesComponent };
